import { z } from 'zod'

// Profil de détection : réglages versionnés, figés dans le snapshot de mission, jamais modifiés par l'opérateur.
// En attendant les contrats 0.4.0 (lot S1 de reca-app-v3), le schéma vit ici.

export const detectionModes = ['OFF', 'OBSERVATION', 'AUTO'] as const
export type DetectionMode = (typeof detectionModes)[number]

export const detectionProfileSchema = z
  .object({
    id: z.string().min(1),
    version: z.number().int().positive(),
    label: z.string().min(1),
    mode: z.enum(detectionModes),
    /** Faux tant que RECA n'a pas validé les seuils sur le terrain. */
    certified: z.boolean(),
    captureRadiusM: z.number().positive(),
    workRadiusM: z.number().positive(),
    exitDistanceM: z.number().positive(),
    minInterventionS: z.number().nonnegative(),
    /** Part minimale du temps observé passée en mouvement : le seul temps passé ne prouve pas un travail. */
    minMovingShare: z.number().min(0).max(1),
    /** Fenêtre de comparaison des positions pour juger du mouvement. */
    movementWindowS: z.number().positive(),
    /** Déplacement sur la fenêtre en deçà duquel le véhicule est tenu pour immobile (au moins la précision). */
    stationaryJitterM: z.number().nonnegative(),
    captureConfirmCount: z.number().int().min(1),
    captureConfirmWindowS: z.number().nonnegative(),
    exitConfirmCount: z.number().int().min(1),
    exitConfirmWindowS: z.number().nonnegative(),
    maxAccuracyM: z.number().positive(),
    maxFixAgeS: z.number().positive(),
    /** Au-delà, l'intervalle entre deux positions n'est pas crédité (pas d'extrapolation à travers un trou). */
    maxGapS: z.number().positive(),
    /** Vitesse implicite au-delà de laquelle une position est un saut incohérent. */
    maxSpeedMps: z.number().positive(),
  })
  .refine((profile) => profile.captureRadiusM < profile.workRadiusM, {
    message: 'Le rayon de capture doit être inférieur au rayon de travail.',
    path: ['captureRadiusM'],
  })
  .refine((profile) => profile.workRadiusM < profile.exitDistanceM, {
    message: 'La distance de sortie doit dépasser le rayon de travail.',
    path: ['exitDistanceM'],
  })

export type DetectionProfile = z.infer<typeof detectionProfileSchema>

/**
 * Valeurs de démonstration de la spécification du 11 oct. 2026 (7 / 35 / 50 m, 120 s, 3 positions sur 5 s,
 * précision 15 m, âge 10 s). Non homologuées : aucune promesse de fonctionnement sur tablette. Part en mouvement,
 * fenêtre et seuil d'immobilité, trou maximal et vitesse maximale sont des hypothèses du moteur, à calibrer.
 */
export const demoProfile: DetectionProfile = detectionProfileSchema.parse({
  id: 'demo',
  version: 3,
  label: 'Démo v0.3',
  mode: 'OBSERVATION',
  certified: false,
  captureRadiusM: 7,
  workRadiusM: 35,
  exitDistanceM: 50,
  minInterventionS: 120,
  minMovingShare: 0.5,
  movementWindowS: 5,
  stationaryJitterM: 3,
  captureConfirmCount: 3,
  captureConfirmWindowS: 5,
  exitConfirmCount: 3,
  exitConfirmWindowS: 5,
  maxAccuracyM: 15,
  maxFixAgeS: 10,
  maxGapS: 10,
  maxSpeedMps: 20,
})
